import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import scoreAPI from '../services/scoreApi';

const VerificationContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 50%, #f1f5f9 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
`;

const VerificationCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 40px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1);
  max-width: 600px;
  width: 100%;
  
  @media (max-width: 768px) {
    padding: 24px;
    margin: 10px;
  }
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 16px 0;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const Subtitle = styled.p`
  font-size: 16px;
  color: #6b7280;
  margin: 0 0 32px 0;
  text-align: center;
  line-height: 1.5;
`;

const MatchInfo = styled.div`
  background: #f8fafc;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 32px;
`;

const MatchInfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const MatchInfoLabel = styled.span`
  font-weight: 600;
  color: #374151;
`;

const MatchInfoValue = styled.span`
  color: #6b7280;
`;

const ScoreboardSection = styled.div`
  margin-bottom: 32px;
`;

const ScoreboardTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 16px 0;
  text-align: center;
`;

const SetHeaders = styled.div`
  display: grid;
  grid-template-columns: 1fr 80px 80px 80px;
  gap: 16px;
  align-items: center;
  margin-bottom: 16px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr 60px 60px 60px;
    gap: 12px;
  }
`;

const SetHeader = styled.div`
  font-weight: 600;
  color: #374151;
  text-align: center;
  font-size: 14px;
`;

const TeamSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
`;

const TeamHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr 80px 80px 80px;
  gap: 16px;
  align-items: center;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr 60px 60px 60px;
    gap: 12px;
  }
`;

const TeamName = styled.div`
  font-weight: 600;
  color: #1f2937;
  font-size: 16px;
`;

const ScoreInputs = styled.div`
  display: grid;
  grid-template-columns: 1fr 80px 80px 80px;
  gap: 16px;
  align-items: center;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr 60px 60px 60px;
    gap: 12px;
  }
`;

const ScoreInput = styled.input`
  width: 100%;
  height: 50px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  text-align: center;
  font-size: 18px;
  font-weight: 600;
  color: #374151;
  background: white;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #3b82f6;
  }

  &:disabled {
    background: #f9fafb;
    color: #9ca3af;
    cursor: not-allowed;
  }
`;

const SubmittedScores = styled.div`
  display: grid;
  grid-template-columns: 1fr 80px 80px 80px;
  gap: 16px;
  align-items: center;
  margin-bottom: 16px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr 60px 60px 60px;
    gap: 12px;
  }
`;

const SubmittedScore = styled.div`
  width: 100%;
  height: 50px;
  border: 2px solid #d1d5db;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 600;
  color: #374151;
  background: #f9fafb;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 16px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
  }
`;

const Button = styled.button`
  flex: 1;
  padding: 14px 24px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  
  @media (max-width: 768px) {
    padding: 12px 20px;
    font-size: 15px;
  }
`;

const ConfirmButton = styled(Button)`
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(16, 185, 129, 0.4);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const DisputeButton = styled(Button)`
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(239, 68, 68, 0.4);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  color: #6b7280;
  font-size: 16px;
  padding: 40px;
`;

const ErrorMessage = styled.div`
  background: #fee2e2;
  color: #dc2626;
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 24px;
  text-align: center;
`;

const SuccessMessage = styled.div`
  background: #d1fae5;
  color: #059669;
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 24px;
  text-align: center;
`;

const ScoreVerificationPage = () => {
  const { t } = useTranslation();
  const { verificationToken } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [verificationData, setVerificationData] = useState(null);
  const [disputedScores, setDisputedScores] = useState({
    team1_set1: '',
    team1_set2: '',
    team1_set3: '',
    team2_set1: '',
    team2_set2: '',
    team2_set3: ''
  });
  const [showDisputeForm, setShowDisputeForm] = useState(false);

  useEffect(() => {
    loadVerificationData();
  }, [verificationToken]);

  const loadVerificationData = async () => {
    try {
      setLoading(true);
      const data = await scoreAPI.getScoreVerificationDetails(verificationToken);
      setVerificationData(data);
      setError(null);
    } catch (err) {
      setError(err.message || t('scoreVerificationPage.loadFailedFallback'));
    } finally {
      setLoading(false);
    }
  };

  const handleScoreChange = (set, team, value) => {
    if (value === '' || (value >= 0 && value <= 99)) {
      setDisputedScores(prev => ({
        ...prev,
        [`team${team}_set${set}`]: value
      }));
    }
  };

  const validateDisputedScores = () => {
    const { team1_set1, team1_set2, team2_set1, team2_set2 } = disputedScores;
    
    if (!team1_set1 || !team1_set2 || !team2_set1 || !team2_set2) {
      setError(t('scoreVerificationPage.errorFillSets'));
      return false;
    }

    // Check if at least one team won 2 sets
    let team1SetsWon = 0;
    let team2SetsWon = 0;

    if (parseInt(team1_set1) > parseInt(team2_set1)) team1SetsWon++;
    else if (parseInt(team2_set1) > parseInt(team1_set1)) team2SetsWon++;

    if (parseInt(team1_set2) > parseInt(team2_set2)) team1SetsWon++;
    else if (parseInt(team2_set2) > parseInt(team1_set2)) team2SetsWon++;

    if (disputedScores.team1_set3 && disputedScores.team2_set3) {
      if (parseInt(disputedScores.team1_set3) > parseInt(disputedScores.team2_set3)) team1SetsWon++;
      else if (parseInt(disputedScores.team2_set3) > parseInt(disputedScores.team1_set3)) team2SetsWon++;
    }

    if (team1SetsWon < 2 && team2SetsWon < 2) {
      setError(t('scoreVerificationPage.errorTwoSets'));
      return false;
    }

    setError(null);
    return true;
  };

  const handleConfirm = async () => {
    try {
      setSubmitting(true);
      setError(null);
      
      await scoreAPI.verifyScore(verificationToken, null, 'confirm');
      
      setSuccess(t('scoreVerificationPage.successConfirmed'));
      setTimeout(() => {
        navigate('/');
      }, 3000);

    } catch (err) {
      setError(err.message || t('scoreVerificationPage.errorConfirmFallback'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDispute = async () => {
    if (!validateDisputedScores()) {
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      
      await scoreAPI.verifyScore(verificationToken, disputedScores, 'dispute');
      
      setSuccess(t('scoreVerificationPage.successDisputed'));
      setShowDisputeForm(false);

    } catch (err) {
      setError(err.message || t('scoreVerificationPage.errorDisputeFallback'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <VerificationContainer>
        <VerificationCard>
          <LoadingMessage>{t('scoreVerificationPage.loading')}</LoadingMessage>
        </VerificationCard>
      </VerificationContainer>
    );
  }

  if (error && !verificationData) {
    return (
      <VerificationContainer>
        <VerificationCard>
          <ErrorMessage>{error}</ErrorMessage>
          <Button type="button" onClick={() => navigate('/')}>{t('scoreVerificationPage.goHome')}</Button>
        </VerificationCard>
      </VerificationContainer>
    );
  }

  return (
    <VerificationContainer>
      <VerificationCard>
        <Title>{t('scoreVerificationPage.title')}</Title>
        <Subtitle>
          {t('scoreVerificationPage.subtitle', { submitter: verificationData?.submittedBy ?? '' })}
        </Subtitle>

        {error && <ErrorMessage>{error}</ErrorMessage>}
        {success && <SuccessMessage>{success}</SuccessMessage>}

        <MatchInfo>
          <MatchInfoRow>
            <MatchInfoLabel>{t('scoreVerificationPage.matchLabel')}</MatchInfoLabel>
            <MatchInfoValue>{verificationData?.team1Name} vs {verificationData?.team2Name}</MatchInfoValue>
          </MatchInfoRow>
          <MatchInfoRow>
            <MatchInfoLabel>{t('scoreVerificationPage.scheduledDateLabel')}</MatchInfoLabel>
            <MatchInfoValue>{verificationData?.scheduledDate}</MatchInfoValue>
          </MatchInfoRow>
          <MatchInfoRow>
            <MatchInfoLabel>{t('scoreVerificationPage.submittedByLabel')}</MatchInfoLabel>
            <MatchInfoValue>{verificationData?.submittedBy}</MatchInfoValue>
          </MatchInfoRow>
        </MatchInfo>

        <ScoreboardSection>
          <ScoreboardTitle>{t('scoreVerificationPage.submittedScores')}</ScoreboardTitle>

          <SetHeaders>
            <SetHeader>{t('scoreVerificationPage.colTeam')}</SetHeader>
            <SetHeader>{t('scoreVerificationPage.colSet1')}</SetHeader>
            <SetHeader>{t('scoreVerificationPage.colSet2')}</SetHeader>
            <SetHeader>{t('scoreVerificationPage.colTieBreaker')}</SetHeader>
          </SetHeaders>

          <TeamSection>
            <TeamHeader>
              <TeamName>{verificationData?.team1Name}</TeamName>
              <div></div>
              <div></div>
              <div></div>
            </TeamHeader>
            <SubmittedScores>
              <div></div>
              <SubmittedScore>{verificationData?.scores?.team1_set1}</SubmittedScore>
              <SubmittedScore>{verificationData?.scores?.team1_set2}</SubmittedScore>
              <SubmittedScore>{verificationData?.scores?.team1_set3 || '-'}</SubmittedScore>
            </SubmittedScores>
          </TeamSection>

          <TeamSection>
            <TeamHeader>
              <TeamName>{verificationData?.team2Name}</TeamName>
              <div></div>
              <div></div>
              <div></div>
            </TeamHeader>
            <SubmittedScores>
              <div></div>
              <SubmittedScore>{verificationData?.scores?.team2_set1}</SubmittedScore>
              <SubmittedScore>{verificationData?.scores?.team2_set2}</SubmittedScore>
              <SubmittedScore>{verificationData?.scores?.team2_set3 || '-'}</SubmittedScore>
            </SubmittedScores>
          </TeamSection>
        </ScoreboardSection>

        {showDisputeForm && (
          <ScoreboardSection>
            <ScoreboardTitle>{t('scoreVerificationPage.correctedScores')}</ScoreboardTitle>

            <SetHeaders>
              <SetHeader>{t('scoreVerificationPage.colTeam')}</SetHeader>
              <SetHeader>{t('scoreVerificationPage.colSet1')}</SetHeader>
              <SetHeader>{t('scoreVerificationPage.colSet2')}</SetHeader>
              <SetHeader>{t('scoreVerificationPage.colTieBreaker')}</SetHeader>
            </SetHeaders>

            <TeamSection>
              <TeamHeader>
                <TeamName>{verificationData?.team1Name}</TeamName>
                <div></div>
                <div></div>
                <div></div>
              </TeamHeader>
              <ScoreInputs>
                <div></div>
                <ScoreInput
                  type="number"
                  min="0"
                  max="99"
                  value={disputedScores.team1_set1}
                  onChange={(e) => handleScoreChange(1, 1, e.target.value)}
                  placeholder="0"
                />
                <ScoreInput
                  type="number"
                  min="0"
                  max="99"
                  value={disputedScores.team1_set2}
                  onChange={(e) => handleScoreChange(2, 1, e.target.value)}
                  placeholder="0"
                />
                <ScoreInput
                  type="number"
                  min="0"
                  max="99"
                  value={disputedScores.team1_set3}
                  onChange={(e) => handleScoreChange(3, 1, e.target.value)}
                  placeholder="0"
                />
              </ScoreInputs>
            </TeamSection>

            <TeamSection>
              <TeamHeader>
                <TeamName>{verificationData?.team2Name}</TeamName>
                <div></div>
                <div></div>
                <div></div>
              </TeamHeader>
              <ScoreInputs>
                <div></div>
                <ScoreInput
                  type="number"
                  min="0"
                  max="99"
                  value={disputedScores.team2_set1}
                  onChange={(e) => handleScoreChange(1, 2, e.target.value)}
                  placeholder="0"
                />
                <ScoreInput
                  type="number"
                  min="0"
                  max="99"
                  value={disputedScores.team2_set2}
                  onChange={(e) => handleScoreChange(2, 2, e.target.value)}
                  placeholder="0"
                />
                <ScoreInput
                  type="number"
                  min="0"
                  max="99"
                  value={disputedScores.team2_set3}
                  onChange={(e) => handleScoreChange(3, 2, e.target.value)}
                  placeholder="0"
                />
              </ScoreInputs>
            </TeamSection>
          </ScoreboardSection>
        )}

        <ButtonContainer>
          {!showDisputeForm ? (
            <>
              <ConfirmButton type="button" onClick={handleConfirm} disabled={submitting}>
                {submitting ? t('scoreVerificationPage.confirming') : t('scoreVerificationPage.confirmScore')}
              </ConfirmButton>
              <DisputeButton type="button" onClick={() => setShowDisputeForm(true)}>
                {t('scoreVerificationPage.disputeScore')}
              </DisputeButton>
            </>
          ) : (
            <>
              <ConfirmButton type="button" onClick={handleDispute} disabled={submitting}>
                {submitting ? t('scoreVerificationPage.submitting') : t('scoreVerificationPage.submitDispute')}
              </ConfirmButton>
              <DisputeButton type="button" onClick={() => setShowDisputeForm(false)}>
                {t('scoreVerificationPage.cancel')}
              </DisputeButton>
            </>
          )}
        </ButtonContainer>
      </VerificationCard>
    </VerificationContainer>
  );
};

export default ScoreVerificationPage;
